/**
 * Stable internal links.
 *
 * Internal references are ordinary Markdown links whose URL uses the `bl://`
 * scheme and an entity ID, so renaming or moving the target never breaks them:
 *
 *   [Study plan](bl://note/<uuid>)                     note or research record
 *   [Sample paper p. 3](bl://paper/<uuid>?page=3)       library item, physical PDF page 3
 *   [@key, p. 12](bl://cite/<uuid>?loc=p.%2012&page=14) citation of a library item
 *   [Fix figure](bl://task/<uuid>)                      task
 *   [Paper draft](bl://manuscript/<uuid>)               manuscript
 *   [data.csv](bl://file/<uuid>)                        uploaded file
 *
 * `page` is always the physical page index in the PDF (1-based). Printed page
 * labels, when known, go in `label` (paper) or `loc` (citation locator).
 */
export const LINK_TYPES = ["note", "paper", "cite", "task", "manuscript", "proposal", "file"] as const;
export type LinkType = (typeof LINK_TYPES)[number];

export interface InternalLink {
  type: LinkType;
  id: string;
  page?: number;
  pageLabel?: string;
  loc?: string;
}

export interface FoundLink extends InternalLink {
  label: string;
  raw: string;
  index: number;
  href: string;
}

const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
const HREF_RE = new RegExp(`^bl://(${LINK_TYPES.join("|")})/(${UUID})(?:\\?([^\\s)#]*))?$`);
const MD_LINK_RE = new RegExp(`\\[((?:[^\\[\\]]|\\[[^\\]]*\\])*)\\]\\((bl://(?:${LINK_TYPES.join("|")})/${UUID}(?:\\?[^\\s)]*)?)\\)`, "g");

export function isUuid(value: unknown): value is string {
  return typeof value === "string" && new RegExp(`^${UUID}$`).test(value);
}

export function parseInternalHref(href: string): InternalLink | null {
  const m = HREF_RE.exec(href.trim());
  if (!m) return null;
  const link: InternalLink = { type: m[1] as LinkType, id: m[2].toLowerCase() };
  if (m[3]) {
    const params = new URLSearchParams(m[3]);
    const page = params.get("page");
    if (page && /^\d+$/.test(page) && Number(page) > 0) link.page = Number(page);
    const label = params.get("label");
    if (label) link.pageLabel = label;
    const loc = params.get("loc");
    if (loc) link.loc = loc;
  }
  return link;
}

export function buildInternalHref(link: InternalLink): string {
  const params = new URLSearchParams();
  if (link.loc) params.set("loc", link.loc);
  if (link.page) params.set("page", String(link.page));
  if (link.pageLabel) params.set("label", link.pageLabel);
  const qs = params.toString();
  return `bl://${link.type}/${link.id}${qs ? `?${qs.replace(/\+/g, "%20")}` : ""}`;
}

export function markdownLink(label: string, link: InternalLink): string {
  const safe = label.replace(/[[\]]/g, "").trim() || "link";
  return `[${safe}](${buildInternalHref(link)})`;
}

/** Find every internal link in Markdown text. */
export function extractInternalLinks(markdown: string): FoundLink[] {
  const out: FoundLink[] = [];
  MD_LINK_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MD_LINK_RE.exec(markdown))) {
    const parsed = parseInternalHref(m[2]);
    if (!parsed) continue;
    out.push({ ...parsed, label: m[1], raw: m[0], index: m.index, href: m[2] });
  }
  return out;
}

/** Replace internal links using a callback that returns replacement Markdown (or null to keep). */
export function rewriteInternalLinks(markdown: string, fn: (link: FoundLink) => string | null): string {
  MD_LINK_RE.lastIndex = 0;
  return markdown.replace(MD_LINK_RE, (raw, label: string, href: string, index: number) => {
    const parsed = parseInternalHref(href);
    if (!parsed) return raw;
    const rep = fn({ ...parsed, label, raw, index, href });
    return rep ?? raw;
  });
}
